// @flow
import {ofType, StateObservable} from 'redux-observable';
import {map, switchMap} from 'rxjs/operators';
import {buildRoute} from '../../http';
import {ActionCreator, buildCatchError, createReducer} from '../../store/utils';
import {SaveGeneralSettingsSuccess} from '../configure/general/duck';
import {SetInitialConfigSuccess} from '../on-boarding/duck';
import {
  endpoints,
  getStoreApi,
  getStorePreviousTierApi,
  getStoreMetadataApi,
} from './api';

import type {ActionsObservable} from 'redux-observable';
import type {Action} from '../../store/utils';
import type {Store} from '../../entities';
import type {GeneralConfigAction} from '../configure/general/duck';
import type {OnBoardingAction} from '../on-boarding/duck';
import {
  GetSessionSuccess,
  LoginSuccess,
  RefreshSessionSuccess,
} from '../auth/duck';
import type {RootState} from '../index';
import type {StoreTier, StoreMetadata} from '../../entities';
import {PerformSubscribeSuccess} from '../checkout/duck';
import {debounceTime} from 'rxjs/operators';

export type StoreAction = Action & {
  store?: Store,
  domain?: string,
  error?: string,
};

export type StoreState = {
  store?: Store,
  storeMetadata?: StoreMetadata,
  isFetchingStore?: boolean,
  storePreviousTier?: StoreTier,
  storeTier?: StoreTier,
  isFetching?: boolean,
  fetchError?: string,
};

export class FetchStore extends ActionCreator<StoreAction> {}
export class FetchStoreSuccess extends ActionCreator<StoreAction> {}
export class FetchStoreErrorFn extends ActionCreator<StoreAction> {}

export class FetchStoreMetadata extends ActionCreator<StoreAction> {}
export class FetchStoreMetadataSuccess extends ActionCreator<StoreAction> {}
export class FetchStoreMetadataErrorFn extends ActionCreator<StoreAction> {}

export class FetchStorePreviousTier extends ActionCreator<StoreAction> {}
export class FetchStorePreviousTierSuccess extends ActionCreator<StoreAction> {}
export class FetchStorePreviousTierErrorFn extends ActionCreator<StoreAction> {}

export const actions = {
  fetchStore: (payload: StoreAction) => new FetchStore(payload),
};

export const initialState = {};

export default createReducer(initialState, {
  [FetchStore.type]: state => ({...state, isFetching: true}),
  [PerformSubscribeSuccess.type]: (state: StoreAction) => ({
    ...state,
    isFetchingStore: true,
  }),
  [FetchStoreSuccess.type]: (_, action: StoreAction) => ({
    store: action.store,
    isFetchingStore: undefined,
  }),
  [SaveGeneralSettingsSuccess.type]: (_, action: GeneralConfigAction) => ({
    store: action.store,
  }),
  [SetInitialConfigSuccess.type]: (
    state: StoreState,
    action: OnBoardingAction
  ) => ({
    store: {...state.store, ...action.store},
  }),
  [FetchStoreMetadataSuccess.type]: (state, action: StoreAction) => ({
    ...state,
    storeMetadata: action.storeMetadata,
  }),
  [FetchStoreMetadataErrorFn.type]: (state, action: StoreAction) => ({
    ...state,
    storeMetadata: undefined,
  }),
  [FetchStoreMetadata.type]: state => ({
    ...state,
    isFetching: true,
  }),
  /*[SaveGeneralSettingsImageSuccess.type]: (
    state: StoreState,
    action: GeneralConfigAction
  ) => ({
    store: action.store,
  }),*/
  [FetchStoreErrorFn.type]: (_, action: StoreAction) => ({
    error: action.error,
  }),
  [FetchStorePreviousTier.type]: state => ({
    ...state,
    storePreviousTier: undefined,
  }),
  [FetchStorePreviousTierSuccess.type]: (state, action: StoreAction) => ({
    ...state,
    storePreviousTier: action.storeTier,
  }),
  [FetchStorePreviousTierErrorFn.type]: (state, action: StoreAction) => ({
    ...state,
    // error: action.error,
  }),
});

export const fetchStoreEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(
      FetchStore.type,
      LoginSuccess.type,
      GetSessionSuccess.type,
      RefreshSessionSuccess.type
    ),
    debounceTime(150),
    switchMap(({domain, user}: StoreAction) => {
      // if (domain) {
      return getStoreApi(undefined, {
        route: buildRoute(endpoints.GET_STORE, [
          domain || window.location.hostname,
        ]),
      }).pipe(
        map((store: Store) => new FetchStoreSuccess({store})),
        buildCatchError(FetchStoreErrorFn)
      );
      // } else return [];
    })
  );

export const fetchStoreMetadataEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(FetchStoreSuccess.type),
    switchMap(() => {
      const storeId = state$.value.store?.store?.id;
      if (!storeId) return [];
      return getStoreMetadataApi(undefined, {
        route: buildRoute(endpoints.GET_STORE_METADATA, [storeId]),
      }).pipe(
        map(
          (storeMetadata: StoreMetadata) =>
            new FetchStoreMetadataSuccess({storeMetadata})
        ),
        buildCatchError(FetchStoreMetadataErrorFn)
      );
    })
  );

export const fetchStorePreviousTierEpic = (
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(FetchStoreSuccess.type),
    switchMap(() => {
      if (
        state$.value &&
        state$.value.store &&
        state$.value.store.store &&
        state$.value.store.store.storeTier &&
        state$.value.store.store.storeTier.name === 'TIER_22'
      ) {
        return getStorePreviousTierApi().pipe(
          map(
            (storeTier: StoreTier) =>
              new FetchStorePreviousTierSuccess({storeTier})
          ),
          buildCatchError(FetchStorePreviousTierErrorFn)
        );
      } else return [];
    })
  );
